import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 1000;

// Registro compartilhado: os listeners de saída da página são instalados UMA vez
// para todos os campos (a auditoria tem dezenas de questões), não um par por campo.
const instances = new Set();

const flushOnHide = () => {
  // Aba escondida/fechando: manda o que está pendente antes que a página morra.
  if (document.visibilityState === 'hidden') {
    instances.forEach((instance) => instance.flush());
  }
};

const warnIfPending = (event) => {
  // F5, fechar aba ou navegar para fora: se ainda há coisa em trânsito, o
  // browser pergunta antes de descartar.
  const hasPending = Array.from(instances).some((instance) => instance.hasPending());
  if (!hasPending) return;
  event.preventDefault();
  event.returnValue = '';
};

const registerInstance = (instance) => {
  if (instances.size === 0) {
    document.addEventListener('visibilitychange', flushOnHide);
    window.addEventListener('beforeunload', warnIfPending);
  }
  instances.add(instance);

  return () => {
    instances.delete(instance);
    if (instances.size === 0) {
      document.removeEventListener('visibilitychange', flushOnHide);
      window.removeEventListener('beforeunload', warnIfPending);
    }
  };
};

// Garante que todo campo com save pendente termine de salvar antes de uma
// navegação explícita (ex.: botão "Voltar" da auditoria) — não depender só do
// blur (pode não disparar) nem do unmount (dispara mas ninguém espera).
export async function flushAllPending() {
  await Promise.all(Array.from(instances).map((instance) => instance.flush()));
}

/**
 * Auto-save por campo: salva sozinho ~1s depois que o usuário para de digitar,
 * na saída do campo, ao trocar de pilar (desmontagem) e ao esconder a aba.
 *
 * Sem isto a nota só ia para a API no `onBlur`, e navegação entre pilares
 * levava junto o que tinha sido digitado (EG-44/EG-45).
 */
export default function useDebouncedSave(save, delay = DEBOUNCE_MS) {
  const [status, setStatus] = useState('idle');

  const timeoutRef = useRef();
  // Última versão do valor ainda não confirmada pela API (null = nada pendente).
  const pendingRef = useRef(null);
  const isSavingRef = useRef(false);
  const isMountedRef = useRef(true);

  // O caller recria a função de save a cada render; guardar em ref evita salvar
  // com estado velho sem precisar reagendar o timer.
  const saveRef = useRef(save);
  saveRef.current = save;

  const setStatusIfMounted = useCallback((next) => {
    if (isMountedRef.current) setStatus(next);
  }, []);

  const flush = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (pendingRef.current === null || isSavingRef.current) return Promise.resolve();

    isSavingRef.current = true;
    setStatusIfMounted('saving');

    // Loop: o que for digitado durante o request vira uma nova volta, para o
    // último valor sempre vencer. Retorna a promise pra quem precisa GARANTIR
    // que o save terminou antes de agir (ex.: navegar pra fora da tela).
    return (async () => {
      let current;
      try {
        while (pendingRef.current !== null) {
          current = pendingRef.current.value;
          pendingRef.current = null;
          await saveRef.current(current);
        }
        setStatusIfMounted('saved');
      } catch {
        // Devolve o valor à fila para o retry — a menos que já haja um mais novo.
        if (pendingRef.current === null && current !== undefined) {
          pendingRef.current = { value: current };
        }
        setStatusIfMounted('error');
      } finally {
        isSavingRef.current = false;
      }
    })();
  }, [setStatusIfMounted]);

  const schedule = useCallback(
    (value) => {
      pendingRef.current = { value };
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(flush, delay);
    },
    [delay, flush]
  );

  const cancel = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    pendingRef.current = null;
  }, []);

  useEffect(() => {
    const unregister = registerInstance({
      hasPending: () => pendingRef.current !== null || isSavingRef.current,
      flush,
    });

    return () => {
      isMountedRef.current = false;
      unregister();
      // Trocar de pilar desmonta o card: o que estava esperando o debounce
      // ainda precisa ir para a API.
      flush();
    };
  }, [flush]);

  return { status, schedule, flush, cancel, retry: flush };
}
